/**
 * SimControlPanel — 3D 가상약달력 우측 컨트롤 (DeviceDetailModal 에서 분리).
 *
 * 슬롯 선택(LED 점멸), 처방대로 리필/전체 리셋, 미복용 감지 즉시 실행(테스트용),
 * MQTT/DB 메시지 로그를 담당한다. 모든 동작은 부모(DeviceDetailModal)의 핸들러로 위임.
 */
import { Button, Card, Segmented, Space, Tag, Typography } from 'antd'
import { useLabel } from '@/framework/useLabel'
import { SLOT_COLOR } from './DispenserScene'

interface Props {
  slotNames: string[]            // 슬롯 표시명 4개 (아침/점심/저녁/취침)
  activeSlot: number             // 현재 LED 점멸 슬롯 (0~3)
  slotTimesText: string          // "아침 08:00 · 점심 12:30 ..." 요약
  logs: string[]                 // MQTT/DB 메시지 로그 (최근 30건)
  onSlotChange: (v: number) => void
  onRefill: () => void
  onReset: () => void
  onRunMissed: () => void        // 미복용 감지 즉시 실행 (sim.cronMissed)
}

export default function SimControlPanel({
  slotNames, activeSlot, slotTimesText, logs, onSlotChange, onRefill, onReset, onRunMissed,
}: Props) {
  const { t } = useLabel()
  return (
    <>
      <Card title={t('TTL_SIM_CONTROL', '시뮬 컨트롤')} style={{ borderRadius: 14, marginBottom: 12 }}>
        <Typography.Text style={{ fontSize: 12, color: '#777', display: 'block', marginBottom: 6 }}>
          {t('LBL_ACTIVE_SLOT', '복용 시간대 — 해당 슬롯 LED 점멸')}
        </Typography.Text>
        <Segmented
          block
          value={activeSlot}
          onChange={(v) => onSlotChange(v as number)}
          options={slotNames.map((n, i) => ({ label: n, value: i }))}
          style={{ marginBottom: 8 }}
        />
        <div style={{ fontSize: 11, color: '#9aa3af', marginBottom: 12 }}>{slotTimesText}</div>
        <Space wrap>
          <Button type="primary" onClick={onRefill}>{t('BTN_REFILL_ALL', '처방대로 리필')}</Button>
          <Button danger onClick={onReset}>{t('BTN_RESET_ALL', '전체 리셋')}</Button>
          <Button onClick={onRunMissed}>{t('BTN_RUN_MISSED', '미복용 감지 실행')}</Button>
        </Space>
        <div style={{ marginTop: 12, fontSize: 12, color: '#777', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {slotNames.map((n, i) => (
            <Tag key={n} style={{ background: SLOT_COLOR[i], color: '#1a1a1a', border: 0 }}>{n}</Tag>
          ))}
          <Tag style={{ background: '#52c41a', color: '#fff', border: 0 }}>{t('LBL_OPEN_SUCCESS', '복용 완료')}</Tag>
          <Tag style={{ background: '#9aa3af', color: '#fff', border: 0 }}>{t('LBL_NO_RX', '처방 없음')}</Tag>
        </div>
      </Card>

      <Card title={t('TTL_MQTT_STREAM', 'MQTT 메시지 stream')} style={{ borderRadius: 14 }} bodyStyle={{ height: 288, overflowY: 'auto' }}>
        {logs.length === 0 ? (
          <span style={{ color: '#999' }}>{t('LBL_NO_TOPIC', '(아직 토픽 없음 — 3D 포켓을 클릭하세요)')}</span>
        ) : (
          <pre style={{ fontFamily: 'monospace', fontSize: 11, margin: 0 }}>{logs.join('\n')}</pre>
        )}
      </Card>
    </>
  )
}
